<template>
  <div class="order-table-container small">
    <b-tabs
      class="mb-3"
      nav-class="filter-tabs"
      fill
      v-model="tabIndex"
      :end="true"
    >
      <b-tab
        v-for="(status, index) in Object.keys(statuses).map(key => statuses[key])"
        :key="index"
        :title="status"
        @click="filterByStatus(status, index)"
      >
      </b-tab>
    </b-tabs>
    <div class="my-2">
      <b-navbar type="light" variant="light" class="actions-bar">
        <b-navbar-nav v-if="userSalesRepPermissions.length > 2" class="ml-1 hide-for-mobile">
          <b-nav-item>
            <multiselect
              v-model="selectedSalesRep"
              :options="userSalesRepPermissions"
              :close-on-select="true"
              :clear-on-select="false"
              select-label=""
              placeholder="Select a Sales Representative"
              track-by="Id"
              :custom-label="customLabel"
              @input="handleSalesRepSelect"
              :searchable="true">
            </multiselect>
          </b-nav-item>
        </b-navbar-nav>
        <b-navbar-nav class="ml-1">
          <b-input-group>
            <b-input-group-prepend
              ><b-input-group-text class="search-icon"
                ><b-icon icon="search"></b-icon></b-input-group-text
            ></b-input-group-prepend>
            <b-form-input
              class="mr-sm-2 search-bar"
              :placeholder="
                isMobile
                  ? 'Sales Order # / PO # / Customer'
                  : 'Search order by Sales Order # / PO # / Customer'
              "
              v-model.trim="searchTerm"
              debounce="500"
              :class="{
                'is-invalid': searchTerm.length > 0 && searchTerm.length < 2,
              }"
            ></b-form-input>
            <div
              v-if="searchTerm.length > 0 && searchTerm.length < 2"
              class="invalid-feedback"
            >
              Search requires minimum of 2 characters
            </div>
          </b-input-group>
        </b-navbar-nav>
        <b-navbar-nav v-if="(isFiltered || isPreFiltered)" class="hide-for-mobile">
          <b-nav-item>
            <b-button
              variant="outline-primary"
              class="primary-outline-btn"
              @click="clearFilters"
              data-cy="reset-filters"
            >
              <b-icon icon="x-circle"></b-icon>
              <b class="ml-2">Reset Filter</b>
            </b-button>
          </b-nav-item>
        </b-navbar-nav>
        <b-navbar-nav class="reset-filter-icon">
          <b-nav-item>
            <b-button
              variant="flat"
              class="primary-outline-btn p-1 color-primary"
              @click="filterModalVisible = true"
            >
              <b-icon
                :icon="(isFiltered || isPreFiltered) ? 'filter-circle-fill' : 'filter-circle'"
                @click="filterModalVisible = true"
              ></b-icon>
            </b-button>
          </b-nav-item>
        </b-navbar-nav>
        <b-navbar-nav class="ml-auto" v-if="userSalesRepPermissions.length > 1 || _isCustomerAdminSite()">
          <b-nav-item v-hideIf.cantWrite="{ module: this.module }">
            <b-button
              variant="info"
              class="background-color-primary"
              @click="onNewOrder"
            >
              <b-icon icon="plus"></b-icon>
              <b class="ml-2 new-order-text">Create Order</b>
            </b-button>
          </b-nav-item>
        </b-navbar-nav>
      </b-navbar>
    </div>
    <div
      style="display: flex; align-items: center; gap: 10px; font-size: medium"
      v-if="isFiltered"
    >
      Filtered By:
      <b-badge
        v-for="(value, key) in activeFilters"
        variant="warning"
        pill
        :key="'filter-badge' + key"
        href="#"
        @click="filter[key] = ''"
        >{{ value }} <b-icon icon="x-circle-fill" class="ml-1"></b-icon
      ></b-badge>
    </div>
    <b-table
      :items="orders.List"
      :fields="fields.filter(x => !x.isHidden)"
      responsive
      hover
      borderless
      class="order-table"
      :busy="orders.loading"
      thead-class="th-custom-class"
      data-cy="order-table-fields"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle primary-color mr-2"></b-spinner>
          <strong class="primary-color">Loading...</strong>
        </div>
      </template>
      <template v-slot:head(details)>
        <div></div>
      </template>
      <template v-slot:cell(details)="row">
        <b-icon
          :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"
          @click="row.toggleDetails"
        >
        </b-icon>
      </template>
      <template v-slot:cell(SalesOrderNumber)="{ item }">
        <router-link
          :to="{
            name: 'order_summary',
            params: { invoiceNo: item.SalesOrderNumber },
          }"
          class="order-link"
        >
          {{ item.SalesOrderNumber }}
        </router-link>
      </template>
      <template v-slot:cell(Customer)="{ item }">
        <div class="d-flex align-items-center">
          <b-icon icon="shop" variant="info" class="mr-2" />
          <div>
            <router-link
              :to="'/customer/'+item.CustomerNumber"
              class="order-link"
            >
              {{ item.CustomerName }}
            </router-link>
            <div>{{ item.BillToParty.Address1 }}</div>
            <div>
              {{ item.BillToParty.City }}, {{ item.BillToParty.State }}
              {{ item.BillToParty.Zip }}
            </div>
          </div>
        </div>
      </template>
      <template
        v-for="field in fields.filter((f) => f.isSortable || f.isFilterable)"
        v-slot:[`head(${field.key})`]="{}"
      >
        <div :key="field.key">
          <div class="d-flex align-items-center">
            <div class="d-flex align-items-center">
              <template>
                {{ field.label }}
              </template>
              <b-dropdown dropright no-caret size="sm" variant="link" boundary="window" block>
                <template #button-content>
                  <b-icon
                    v-if="field.isFilterable"
                    :icon="
                      filter[field.key] !== ''
                        ? 'filter-circle-fill'
                        : 'filter-circle'
                    "
                    :variant="filter[field.key] !== '' ? 'primary' : 'secondary'"
                    class="header-icon order-filters"
                    :id="'filter-' + field.key"
                  ></b-icon>
                </template>
                <div class="px-2">
                  <label class="d-flex align-items-center small">
                    <template v-if="field.key ==='Deliveries' && statuses.ALL === activeStatus">
                      Ship Via
                    </template>
                    <template v-else>
                      {{ field.label }}
                    </template>
                    <template v-if="field.isSortable">
                      <b-icon
                        v-if="sortOptions.by === field.key"
                        @click="sort(field.key)"
                        :icon="sortOptions.desc ? 'sort-down' : 'sort-up'"
                        class="header-icon ml-2 order-filters"
                      ></b-icon>
                      <b-icon
                        v-else
                        @click="sort(field.key)"
                        :icon="'sort-down'"
                        class="header-icon ml-2 order-filters"
                        style="color: #000 !important"
                      ></b-icon>
                    </template>
                  </label>
                  <b-input-group size="sm" class="mt-2">
                    <b-form-input
                      :id="'filter' + field.key"
                      v-if="field.isDate"
                      v-model.trim="filter[field.key]"
                      type="date"
                      size="sm"
                      placeholder="YYYY-MM-DD"
                      autocomplete="off"
                      debounce="1000"
                    ></b-form-input>
                    <b-form-input
                      :id="'filter' + field.key"
                      v-else
                      type="text"
                      :placeholder="generatePlaceHolder(field)"
                      v-model.trim="filter[field.key]"
                      debounce="1000"
                    ></b-form-input>
                    <b-input-group-append v-if="filter[field.key].length > 0"
                      ><b-input-group-text
                        class="clear-btn"
                        @click="filter[field.key] = ''"
                        ><b-icon icon="x"></b-icon></b-input-group-text
                    ></b-input-group-append>
                  </b-input-group>
                </div>
              </b-dropdown>
            </div>

            <template v-if="field.isSortable">
              <b-icon
                v-if="sortOptions.by === field.key"
                @click="sort(field.key)"
                :icon="sortOptions.desc ? 'sort-down' : 'sort-up'"
                class="header-icon ml-2 order-filters"
              ></b-icon>
            </template>
          </div>
        </div>
      </template>
      <template v-slot:cell(Material)="{ item }">
        <div class="align-items-center">
          <div>
            <div class="font-weight-bold">
              {{ item.OrderItems.length }} items(s)
            </div>
            <div v-for="i in item.OrderItems.slice(0, 2)" :key="i.ItemNumber">
              <div class="row mt-1">
                <div class="col-sm-3 text-wrap text-break">
                  {{ i.MaterialNumber }}
                </div>
                <div class="col-sm-7 text-wrap text-break">
                  {{ i.Description }}
                </div>
                <div class="col-sm-2 text-wrap text-break">
                  {{ i.Quantity }}
                </div>
              </div>
            </div>
          </div>
          <div class="" v-if="item.OrderItems.length > 2"><b>+More</b></div>
        </div>
      </template>
      <template v-slot:cell(Deliveries)="{ item }">
        <div
          class="d-flex align-items-center"
          v-if="item.Deliveries != null && item.Deliveries.length > 0"
        >
          <b-icon icon="truck" variant="info" class="mr-2" />
          <div>
            <template v-for="delivery in item.Deliveries.slice(0,2)">
              <template v-if="delivery.ShippingInformation && delivery.ShippingInformation.TrackingUpdatesList?.length > 0">
                <b-button
                  @click="() => setSelectedShippingInformation(delivery.ShippingInformation, delivery.TrackingNumber)"
                  variant="link p-0"
                  :key="delivery.DeliveryId"
                  >{{ delivery.Carrier }}</b-button
                >
              </template>
              <template v-else>
                {{ delivery.Carrier }}
              </template>
            </template>
          </div>
          <div v-if="item.Deliveries != null && item.Deliveries.length > 2"> + {{ item.Deliveries.length-2 }}</div>
        </div>
        <div
          class="d-flex align-items-center"
          v-else-if="item.ExpectedShippingDate"
        >
          <p title="Expected Shipping Date">
            {{ formatDate(item.ExpectedShippingDate) }}
          </p>
        </div>
      </template>
      <template v-slot:cell(OrderStatus)="{ item }">
        <span
          :class="
            'rounded border p-2 text-black status status_' +
            item.OrderStatus.toLowerCase().replace(/ /g, '_')
          "
        >
          {{ item.OrderStatus }}
        </span>
      </template>
      <template #row-details="row">
        <b-card>
          <b-row class="mb-2">
            <b-col cols="4" class="row-details-label">Customer:</b-col>
            <b-col cols="8"
              ><div class="text-left">
                <div>
                  <div>{{ row.item.CustomerName }}</div>
                  <div>{{ row.item.BillToParty.Address1 }}</div>
                  <div>
                    {{ row.item.BillToParty.City }},
                    {{ row.item.BillToParty.State }}
                    {{ row.item.BillToParty.Zip }}
                  </div>
                </div>
              </div></b-col
            >
          </b-row>
          <b-row class="mb-2">
            <b-col cols="4" class="row-details-label"
              >Material <br />({{ row.item.OrderItems.length }} items):</b-col
            >
            <b-col cols="8">
              <div class="text-left">
                <div>
                  <div
                    v-for="i in row.item.OrderItems.slice(0, 2)"
                    :key="i.ItemNumber"
                  >
                    <div class="text-wrap text-break">
                      <b class="color-primary">{{ i.Quantity }}</b> of
                      {{ i.MaterialNumber }} ({{ i.Description }})
                    </div>
                  </div>
                </div>
                <div class="" v-if="row.item.OrderItems.length > 2">
                  <b>+More</b>
                </div>
              </div></b-col
            >
          </b-row>
          <b-row class="mb-2">
            <b-col cols="4" class="row-details-label">Ship Via</b-col>
            <b-col cols="8">
              <div
                class="d-flex align-items-center"
                v-if="
                  row.item.Deliveries != null && row.item.Deliveries.length > 0
                "
              >
                <b-icon icon="truck" variant="info" class="mr-2" />
                <div>
                  <template v-for="delivery in row.item.Deliveries">
                    <template v-if="delivery.ShippingInformation && delivery.ShippingInformation.TrackingUpdatesList?.length > 0">
                      <b-button
                        @click="() => setSelectedShippingInformation(delivery.ShippingInformation, delivery.TrackingNumber)"
                        variant="link p-0"
                        :key="delivery.DeliveryId"
                        >{{ delivery.Carrier }}</b-button
                      >
                    </template>
                    <template v-else>
                      {{ delivery.Carrier }}
                    </template>
                  </template>
                </div>
              </div>
              <div
                class="d-flex align-items-center"
                v-else-if="row.item.ExpectedShippingDate"
              >
                <p title="Expected Shipping Date">
                  {{ formatDate(row.item.ExpectedShippingDate) }}
                </p>
              </div></b-col
            >
          </b-row>
          <b-row class="mb-2">
            <b-col cols="4" class="row-details-label">Status</b-col>
            <b-col cols="8">
              <span
                :class="
                  'rounded border p-1 text-black status status_' +
                  row.item.OrderStatus.toLowerCase().replace(/ /g, '_')
                "
              >
                {{ row.item.OrderStatus }}
              </span>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="4" class="row-details-label">Total Price</b-col>
            <b-col cols="8 text-left">
              {{ formatPrice(row.item.Total) }}
            </b-col>
          </b-row>
          <b-button
            size="sm"
            class="mt-2 w-100"
            @click="row.toggleDetails"
            variant="outline"
            ><b-icon icon="chevron-double-up" variant="link"
          /></b-button>
        </b-card>
      </template>
    </b-table>
    <div class="d-flex justify-content-center" md="my-5">
      <b-pagination
        v-if="orders.Page > 0"
        v-model="orders.Page"
        :total-rows="orders.Total"
        :per-page="orders.PageSize"
        limit="10"
        @change="(page) => pageChanged(page, activeStatus)"
      >
      </b-pagination>
    </div>
    <b-modal
      v-model="filterModalVisible"
      title="Filter Data"
      centered
      size="xl"
      footer-class="justify-content-center"
    >
      <b-container fluid>
        <b-row>
          <b-col class="mb-3">
            <multiselect
              v-model="selectedSalesRep"
              :options="userSalesRepPermissions"
              :close-on-select="true"
              :clear-on-select="false"
              select-label=""
              placeholder="Select a Sales Representative"
              track-by="Id"
              :custom-label="customLabel"
              @input="handleSalesRepSelect"
              :searchable="true">
            </multiselect>
          </b-col>
        </b-row>
        <b-row>
          <!-- Loop through filterable fields to generate columns -->
          <template v-for="field in fields.filter((f) => f.isFilterable)">
            <b-col :key="field.key" cols="12">
              <div class="mb-3">
                <label :for="field.key" class="small" v-if="field.key ==='Deliveries' && statuses.ALL === activeStatus">Ship Via</label>
                <label :for="field.key" class="small" v-else>{{ field.label }}</label>
                <b-icon
                  v-if="sortOptions.by === field.key"
                  @click="sort(field.key)"
                  :icon="sortOptions.desc ? 'sort-down' : 'sort-up'"
                  class="header-icon ml-2"
                ></b-icon>
                <b-icon
                  v-else
                  @click="sort(field.key)"
                  :icon="'sort-down'"
                  class="header-icon ml-2"
                  style="color: lightgray !important"
                ></b-icon>
                <b-input-group size="text-sm">
                  <b-input-group-prepend v-if="field.isDate || field.isOptionalDate">
                    <b-form-datepicker
                      v-model.trim="filter[field.key]"
                      button-only
                      size="sm"
                      locale="en-US"
                      button-variant="link"
                    ></b-form-datepicker>
                  </b-input-group-prepend>
                  <b-form-input
                    v-if="field.isDate"
                    v-model.trim="filter[field.key]"
                    type="text"
                    size="sm"
                    placeholder="YYYY-MM-DD"
                    autocomplete="off"
                    debounce="1000"
                  ></b-form-input>
                  <b-form-input
                    v-else
                    type="text"
                    :placeholder="generatePlaceHolder(field)"
                    v-model.trim="filter[field.key]"
                    debounce="1000"
                  ></b-form-input>
                  <b-input-group-append v-if="filter[field.key].length > 0"
                    ><b-input-group-text
                      class="clear-btn"
                      @click="filter[field.key] = ''"
                      ><b-icon icon="x"></b-icon></b-input-group-text
                  ></b-input-group-append>
                </b-input-group>
              </div>
            </b-col>
          </template>
        </b-row>
      </b-container>
      <div slot="modal-footer" class="filter-modal-footer">
        <div class="text-center my-2" v-if="orders.loading">
          <b-spinner class="align-middle primary-color mr-2"></b-spinner>
          <strong class="primary-color">Filtering...</strong>
        </div>
        <b-button
          variant="outline-primary"
          class="w-100 color-primary mb-2"
          @click="filterModalVisible = false"
          v-else
          size="sm"
        >
          <b class="ml-2">See Results</b>
        </b-button>
        <b-button
          variant="outline-default"
          class="w-100"
          @click="clearFilters"
          size="sm"
          v-if="(isFiltered || isPreFiltered) && !orders.loading"
          data-cy="reset-filters"
        >
          <b-icon icon="x-circle"></b-icon>
          <b class="ml-2">Reset Filter</b>
        </b-button>
      </div>
    </b-modal>
    <order-tracking
      :shippingInformation="selectedShippingInformation"
      :trackingNumber="selectedTrackingNumber"
      @close="() => { selectedShippingInformation = undefined;  selectedTrackingNumber = ''}"></order-tracking>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import dayjs from 'dayjs'
import Multiselect from 'vue-multiselect'

// TODO: MxOrders?
import MxSite from '@/mixins/site'
import { IOrder, IOrderFilter, IOrderFilterOn, ISearchFilters, ShippingInformation } from '@/store/orders/types'

import LoadingText from '@/components/utility/loading-text.vue'
import { CartItem } from '@/store/cart/types'
import { blankSearchFilters, OrderStatus } from '@/store/orders/constants'
import { ResponseArray } from '@/store/types'
import OrderTracking from '@/components/order-tracking/order-tracking.vue'
import { isMobile, handleResize } from '@/common/helpers/responsive-helper'
import { Modules, ResourcePermission, ResourceType } from '@/store/security/types'
import { Api } from '@/api/Api'
import { isCustomerAdminSite } from '@/helpers/sites.helper'
import _ from 'lodash'

const nsOrders = namespace('orders')
const nsOrderState = namespace('order-state')

@Component({
  components: {
    'loading-text': LoadingText,
    Multiselect,
    'order-tracking': OrderTracking,
  },
})
export default class extends Mixins(MxSite) {
  private api = new Api()

  public module = Modules.OrderAdmin

  public statuses = OrderStatus

  public selectedShippingInformation: ShippingInformation | any = { isBlank: true }

  public selectedTrackingNumber = ''

  public tabIndex = 0

  public currentPage = 1

  public filter: IOrderFilter = {
    SalesOrderNumber: '',
    OrderDate: '',
    CustomerPurchaseOrderNumber: '',
    Customer: '',
    Deliveries: '',
  }

  public filterTooltip: IOrderFilterOn = {
    SalesOrderNumber: false,
    OrderDate: false,
    CustomerPurchaseOrderNumber: false,
    Customer: false,
    Deliveries: false,
  }

  public searchTerm = ''

  public async handleSalesRepSelect() {
    this.filterBySearch()
  }

  public sortOptions: { by: string; desc: boolean } = {
    by: 'OrderDate',
    desc: true,
  }

  public isPreFiltered = false

  @nsOrders.Action('getOrders')
  private getOrders: any

  @nsOrders.Action('fetchOrder')
  private fetchOrder: any

  @nsOrderState.Action('resetOrder')
  private resetOrder: any

  @nsOrders.State('orders')
  public orders!: ResponseArray<IOrder>

  @nsOrders.State('searchFilters')
  public searchFilters!: ISearchFilters

  @nsOrders.Action('resetSearchFilters')
  private resetSearchFilters: any

  public isMobile = isMobile()

  public userSalesRepPermissions: any = []

  public created() {
    this.initializeSearch()
    handleResize((isMobile) => {
      this.isMobile = isMobile
    })
  }

  public onNewOrder() {
    this.$router.push({
      name: 'new-order',
    })
  }

  public onNewInquiry() {
    this.$router.push({
      name: 'new-inquiry',
    })
  }

  public get activeFilters(): any {
    const filtered = {}
    for (const key in this.filter) {
      if (this.filter[key] !== '') {
        filtered[key] = this.filter[key]
      }
    }
    return filtered
  }

  public filterModalVisible = false

  public activeStatus = this.statuses.ALL

  public selectedSalesRep: ResourcePermission | null = null

  public mounted() {
    // TODO jm
    if (this._isCustomerAdminSite() && this.userSalesRepPermissions.length <= 0) {
      this.api.fetchV2({ url: '/sales-rep?includePendingPermissions=false&includeUsers=false&pageSize=10000&page=1' })
        .then((salesRepsList) => {
          this.userSalesRepPermissions = [{ ResourceName: 'Select a Sales Representative', Id: null }, ...salesRepsList.List.map((item: any) => { return { Id: item.Id, ResourceName: item.Name } })]
        })
    } else {
      const permissions = (this.currentUser.ResourcePermissions ?? []).filter(item => item.Type === ResourceType.SalesRep)
      this.userSalesRepPermissions = [{ ResourceName: 'Select a Sales Representative', Id: null }, ...permissions.map((item: any) => { return { Id: item.ResourceId, ResourceName: item.ResourceName } })]
    }
  }

  // new method to filter orders by status
  public filterByStatus(status: string, index: number) {
    this.activeStatus = status
    this.tabIndex = index
  }

  public sort(field: string) {
    if (this.sortOptions.by === field) {
      this.sortOptions = {
        ...this.sortOptions,
        desc: !this.sortOptions.desc,
      }
    } else {
      this.sortOptions = {
        by: field,
        desc: true,
      }
    }
  }

  public setSelectedShippingInformation(information: ShippingInformation, trackingNumber: string) {
    this.selectedTrackingNumber = trackingNumber
    this.selectedShippingInformation = information
  }

  public initializeSearch() {
    if (this.isObjectPrefiltered(this.searchFilters)) {
      this.isPreFiltered = true
    }

    this.filter = {
      ...this.filter,
      OrderDate: this.searchFilters.orderDate,
      SalesOrderNumber: this.searchFilters.salesOrderNumber,
      CustomerPurchaseOrderNumber: this.searchFilters.poNumber,
      Customer: this.searchFilters.customerName,
      Deliveries: this.searchFilters.delivery,
    }

    this.sortOptions = {
      by: this.searchFilters.sortBy,
      desc: this.searchFilters.sortDesc,
    }

    this.searchTerm = this.searchFilters.keyTerm

    this.activeStatus = this.searchFilters.orderStatus

    this.tabIndex = this.searchFilters.tabIndex
    this.getOrders({
      ...this.searchFilters,
    })
  }

  @Watch('sortOptions')
  @Watch('filter', { deep: true })
  @Watch('activeStatus')
  public filterBySearch() {
    if (this.validateFilters() && !this.orders.loading) {
      this.getOrders({
        page: 1,
        orderStatus: this.activeStatus,
        sortBy: this.sortOptions.by,
        sortDesc: this.sortOptions.desc,
        salesOrderNumber: this.filter.SalesOrderNumber,
        customerName: this.filter.Customer,
        poNumber: this.filter.CustomerPurchaseOrderNumber,
        orderDate: this.filter.OrderDate,
        keyTerm: this.searchTerm,
        salesRep: this.selectedSalesRep?.Id,
        tabIndex: this.tabIndex,
        delivery: this.filter.Deliveries,
      })
    }
  }

  private validateFilters(): boolean {
    this.filterTooltip = {
      SalesOrderNumber: false,
      OrderDate: false,
      CustomerPurchaseOrderNumber: false,
      Customer: false,
      Deliveries: false,
    }
    if (this.filter.OrderDate && !this.validateDate(this.filter.OrderDate)) {
      this.filterTooltip.OrderDate = true
      return false
    }
    return true
  }

  private validateDate(date: string): boolean {
    return dayjs(date, 'YYYY-MM-DD', true).isValid()
  }

  @Watch('searchTerm')
  public filterBySearchTerm() {
    if ((this.searchTerm.length >= 2 || this.searchTerm.length === 0) && !this.orders.loading) {
      this.getOrders({
        page: 1,
        orderStatus: this.activeStatus,
        sortBy: this.sortOptions.by,
        sortDesc: this.sortOptions.desc,
        salesOrderNumber: this.filter.SalesOrderNumber,
        customerName: this.filter.Customer,
        poNumber: this.filter.CustomerPurchaseOrderNumber,
        orderDate: this.filter.OrderDate,
        keyTerm: this.searchTerm,
        salesRep: this.selectedSalesRep?.Id,
        tabIndex: this.tabIndex,
        delivery: this.filter.Deliveries,
      })
    }
  }

  public get isFiltered(): boolean {
    for (const key in this.filter) {
      if (
        Object.prototype.hasOwnProperty.call(this.filter, key) &&
        this.filter[key] !== ''
      ) {
        return true // At least one field is not empty
      }
    }
    return false // All fields are empty
  }

  public isObjectPrefiltered(filters) {
    for (const key in filters) {
      if (
        Object.prototype.hasOwnProperty.call(filters, key) && filters[key] &&
        filters[key] !== blankSearchFilters[key]
      ) {
        return true // At least one field is not empty
      }
    }
    return false // All fields are empty
  }

  public clearFilters() {
    this.filter = {
      SalesOrderNumber: '',
      OrderDate: '',
      CustomerPurchaseOrderNumber: '',
      Customer: '',
      Deliveries: '',
    }

    this.filterTooltip = {
      SalesOrderNumber: false,
      OrderDate: false,
      CustomerPurchaseOrderNumber: false,
      Customer: false,
      Deliveries: false,
    }

    this.filterTooltip = {
      SalesOrderNumber: false,
      OrderDate: false,
      CustomerPurchaseOrderNumber: false,
      Customer: false,
      Deliveries: false,
    }

    this.sortOptions = {
      by: 'OrderDate',
      desc: true,
    }

    this.activeStatus = this.statuses.ALL
    this.tabIndex = 0
    this.searchTerm = ''
    this.resetSearchFilters()
    this.isPreFiltered = false
  }

  public selectAll = false

  public selectedRows: IOrder[] = []
  public items: IOrder[] = []

  public get selectedItems(): IOrder[] {
    return this.orders.List.filter((item) => this.selectedRows.includes(item))
  }

  public updateCheckBox(item: IOrder) {
    const index = this.selectedRows.findIndex(
      (x) => x.SalesOrderNumber === item.SalesOrderNumber,
    )
    if (index >= 0) {
      this.selectedRows.splice(index, 1)
    } else {
      this.selectedRows.push(item)
    }
  }

  public selectAllItems(event: any) {
    this.selectAll = event.target.checked
    if (this.selectAll) {
      this.selectedRows = [...this.orders.List]
    } else {
      this.selectedRows = []
    }
  }

  public formatPrice = (value) => {
    return (
      '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })
    )
  }

  public pageChanged(page: number, orderStatus: string) {
    this.getOrders({
      page,
      orderStatus,
      sortBy: this.sortOptions.by,
      sortDesc: this.sortOptions.desc,
      salesOrderNumber: this.filter.SalesOrderNumber,
      customerName: this.filter.Customer,
      poNumber: this.filter.CustomerPurchaseOrderNumber,
      orderDate: this.filter.OrderDate,
      keyTerm: this.searchTerm,
      salesRep: this.selectedSalesRep?.Id,
      delivery: this.filter.Deliveries,
    })
  }

  public loadOrder(salesOrderNumber: string) {
    this.fetchOrder(salesOrderNumber)
    this.$router.push({
      name: 'order_summary',
      params: { invoiceNo: salesOrderNumber },
    })
  }

  public calculaturePrice(items: CartItem[]) {
    return items.reduce((total, item) => total + item.quantity * item.price, 0)
  }

  public formatDate(date) {
    return dayjs(date).format('YYYY-MM-DD')
  }

  public get fields(): any {
    return [
      {
        key: 'details',
        label: 'Toggle Details',
        thClass: 'responsive-col-hide text-left',
        tdClass: 'responsive-col-hide text-left',
        thStyle: { width: '10%' },
      },
      {
        key: 'SalesOrderNumber',
        label: this.isMobile ? 'Sales Order #' : 'Sales Order Number',
        thClass: 'text-left',
        tdClass: 'responsive-col-size text-left',
        thStyle: 'resp-th-30',
        isSortable: true,
        isFilterable: true,
        hideInlineFilter: true,
      },
      {
        key: 'OrderDate',
        label: this.isMobile ? 'Date' : 'Sales Order Date',
        thClass: 'text-left',
        tdClass: 'responsive-col-size text-left',
        thStyle: 'resp-th-30',
        formatter: (val: string) => dayjs(val).format('YYYY-MM-DD'),
        isSortable: true,
        isFilterable: true,
        isDate: true,
        hideInlineFilter: true,
      },
      {
        key: 'CustomerPurchaseOrderNumber',
        label: 'PO Number',
        thClass: 'text-left',
        tdClass: 'responsive-col-size text-left',
        thStyle: 'resp-th-30',
        isSortable: true,
        isFilterable: true,
        hideInlineFilter: true,
      },
      {
        key: 'Customer',
        label: 'Customer',
        thClass: 'responsive-col text-left',
        tdClass: 'responsive-col text-left',
        thStyle: { width: '15%' },
        isSortable: true,
        isFilterable: true,
      },
      {
        key: 'Material',
        label: 'Material / Description',
        thClass: 'responsive-col text-left',
        tdClass: 'responsive-col text-left',
        thStyle: { width: '30%' },
      },
      {
        key: 'Deliveries',
        label: this.activeStatus === this.statuses.ALL ? 'Estimated Ship Date / Ship Via' : this.activeStatus === this.statuses.SHIPPED ? 'Ship Via' : 'Estimated Ship Date',
        placeholder: 'Ship Via',
        thClass: 'responsive-col text-left',
        tdClass: 'responsive-col text-left',
        formatter: (deliveries: any) => {
          return deliveries[0]?.Carrier ?? ''
        },
        isSortable: false,
        isFilterable: [this.statuses.ALL, this.statuses.SHIPPED].includes(this.activeStatus),
        thStyle: { width: '12%' },
        isHidden: this.activeStatus === this.statuses.CANCELLED,
      },
      {
        key: 'OrderStatus',
        label: 'Status',
        thClass: 'responsive-col text-center',
        tdClass: 'responsive-col text-center w-full',
        thStyle: { width: '10%' },
      },
      {
        key: 'Total',
        label: 'Total Price',
        thClass: 'responsive-col text-right',
        tdClass: 'responsive-col text-right',
        thStyle: { width: '10%' },
        formatter: this.formatPrice,
        isSortable: false,
        isFilterable: false,
      },
    ]
  }

  public customLabel({
    Id,
    ResourceId,
    ResourceName,
  }: {
    Id: string
    ResourceId: string
    ResourceName: string
  }) {
    return ResourceId ? `${ResourceName} - ${ResourceId}` : Id ? `${ResourceName} - ${Id}` : ResourceName
  }

  public _isCustomerAdminSite() {
    return isCustomerAdminSite(this.role)
  }

  public generatePlaceHolder(field : any): string {
    const fieldLabel = _.has(field, 'placeholder') ? field.placeholder : (field as any).label
    return 'Enter ' + fieldLabel
  }
}
</script>
<style>
/* Add custom styles for b-tab */
.nav-link.active {
  position: relative;
}
.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #007bff;
}

.header-icon {
  font-size: 18px; /* Adjust the size as needed */
  color: #0b67b2 !important;
  cursor: pointer;
}

.clear-btn:hover {
  font-size: 18px; /* Adjust the size as needed */
  background-color: lightcoral !important;
  cursor: pointer;
  color: white;
}

.date-picker-btn {
  background: transparent;
}

.filter-tabs {
  flex-wrap: nowrap !important;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 1.5px;
  flex-direction: row !important;
}

.reset-filter-icon {
  display: none !important;
}

@media (max-width: 767px) {
  .new-order-text {
    display: none;
  }
  .actions-bar {
    padding: 5px !important;
  }
  .order-link {
    font-size: 1em !important;
    padding-left: 0 !important;
  }

  .reset-filter-icon {
    display: block !important;
  }
}

.row-details-label {
  color: #000;
  font-weight: 500;
  text-align: right;
}

.th-custom-class {
  position: sticky;
  top: 0px;
  z-index: 2;
}
</style>
